import "../card.scss";

import { FormattedMessage } from "react-intl";
import { type ReactNode, useState } from "react";
import { CardNumberElement, useElements, useStripe } from "@stripe/react-stripe-js";

import { useForm } from "common/core/form";
import { StripeCardElementsUIForReactHookForm } from "common/settings/payment/util";
import type { Payer } from "graphql_globals";

import PaymentFailure from "../card_failure_modal";
import Button from "../../../core/button";
import { useMutation } from "../../../../util/graphql";
import AddCardMutation from "../graphql/add_card_to_organization_mutation.graphql";
import Styles from "./card_form.module.scss";
import { captureException } from "../../../../util/exception";

type CardFormValues = {
  cardName: string;
  cardNumber: string;
  cardExpiry: string;
  cardCvc: string;
  addressZip?: string;
};

type CardFormProps = {
  cardDetails?: { name: string | null; last4: string | null } | null;
  organizationId: string;
  payer: Payer;
  onChangesSaved: () => void;
  submitButtonLabel?: ReactNode;
};

function useSubmitCard() {
  const addCardMutateFn = useMutation(AddCardMutation);
  const stripe = useStripe();
  const elements = useElements();

  return async (
    cardName: string,
    organizationId: string,
    payer: Payer,
    onChangesSaved: () => void,
  ) => {
    if (!elements || !stripe) {
      throw new Error(
        "verify component calling submitCardPayment is wrapped in a stripe <Element>",
      );
    }

    const cardElement = elements.getElement(CardNumberElement);

    try {
      const response = await stripe.createToken(cardElement!, { name: cardName });
      if (response.error) {
        throw new Error(response.error.message);
      }

      const token = response.token.id;

      addCardMutateFn({
        variables: {
          input: {
            id: organizationId,
            cardToken: token,
            defaultPayer: payer,
          },
        },
      })
        .then(() => {
          onChangesSaved();
        })
        .catch((error) => {
          captureException(error);
          throw new Error("Error saving card details");
        });
    } catch (error) {
      captureException(error);
      throw new Error(`Error submitting card to Stripe`);
    }
  };
}

export function CardForm({
  organizationId,
  payer,
  onChangesSaved,
  submitButtonLabel,
}: CardFormProps) {
  const [stripeError, setStripeError] = useState(false);
  const cardForm = useForm<CardFormValues>();
  const submitCard = useSubmitCard();

  const closeModal = () => setStripeError(false);

  const handleCardFormSubmit = (formValues: CardFormValues) => {
    submitCard(formValues.cardName, organizationId, payer, onChangesSaved).catch(() => {
      setStripeError(true);
    });
  };

  return (
    <form onSubmit={cardForm.handleSubmit(handleCardFormSubmit)}>
      <PaymentFailure isOpen={stripeError} redirect={closeModal} />

      <StripeCardElementsUIForReactHookForm
        changeCardElement={() => {}}
        form={cardForm}
        showLabels
      />

      <Button
        type="submit"
        buttonColor="action"
        variant="primary"
        isLoading={cardForm.formState.isSubmitting}
        className={Styles.saveButton}
        disabled={!cardForm.formState.isValid || cardForm.formState.isSubmitting}
      >
        {submitButtonLabel ? (
          submitButtonLabel
        ) : (
          <FormattedMessage
            id="e7a89f11-9b88-4107-94b5-f42881f18dcb"
            defaultMessage="Save payment"
          />
        )}
      </Button>
    </form>
  );
}
